'use strict';

/* Universal analytics constants */

var PAGE_NAMES = {
    cart: 'Cart',
    emptyCart: 'Empty Cart',
    home: 'Home',
    look: 'Product Look Detail',
    pdp: 'Product Detail',
    plp: 'Product Listing',
    search: 'Search results',
};

/**
 * Types of page
 * Values used in page designer too.
 *
 * @readonly
 * @enum {string}
 */
var PAGE_TYPES = {
    account: 'Cuenta',
    campaign: 'Campaign',
    cart: 'Cart',
    category: 'Categoria',
    checkout: 'Checkout',
    home: 'Inicio',
    info: 'Info',
    login: 'Login',
    payment: 'Metodo de pago',
    product: 'Producto',
    product_look: 'product_look',
    product_look_pdp: 'product_look_pdp',
    purchase: 'Purchase',
    search: 'Busqueda',
    searchModal: 'searchModal',
};

/**
 * Constants defining all possible locations where a Product Tile can appear
 * across the website.
 */
const PRODUCT_TILE_LOCATION = {
    /**
     * ProductTile displayed in the cart view,
     * under the recommended products section.
     */
    cartRecommendedProducts: 'cart-recommended-products',
    /**
     * ProductTile displayed in the empty cart view,
     * under the recommended products section.
     */
    emptyCartRecommendedProducts: 'empty-cart-recommended-products',
    /**
     * ProductTile displayed in a cross-selling grid,
     * usually showing complementary or related items.
     */
    crossSellingGrid: 'cross-selling-grid',
    /**
     * ProductTile displayed in the Recommended Einstein Products
     * component (slider/grid layout) on the home page.
     */
    homeRecommendedEinsteinProducts: 'home-recommended-einstein-products',
    /**
     * ProductTile shown in the “Shop the Look” section
     * on the home page, featuring curated outfits.
     */
    homeShopTheLook: 'home-shop-the-look',
    /**
     * ProductTile displayed in the “Complete the Look”
     * section of a Product Detail Page (PDP).
     */
    pdpCompleteTheLook: 'pdp-complete-the-look',
    /**
     * ProductTile displayed in the recommended products
     * section of a Product Detail Page (PDP).
     */
    pdpRecommendedProducts: 'pdp-recommended-products',
    /**
     * ProductTile rendered in a Product Listing Page (PLP)
     * grid — any standard product list.
     */
    plpGrid: 'plp-grid',
    /**
     * ProductTile displayed inside a slider banner
     * component within a PLP view.
     */
    plpSliderBanner: 'plp-slider-banner',
    /**
     * ProductTile displayed in a search result list,
     * from the main or modal search view.
     */
    search: 'search',
    /**
     * ProductTile displayed on the initial search page,
     * before the user enters any query (search modal).
     */
    searchModal: 'search-modal',
};

/**
 * Types of experience components defined for a page designer
 * Values used in page designer too.
 *
 * @readonly
 * @enum {string}
 */
var EXPERIENCE_COMPONENT_TYPES = {
    banner: 'banner',
    categoryFeatured: 'categoryFeatured',
    categoryFeaturedProducts: 'categoryFeaturedProducts',
    contentAsset: 'contentAsset',
    freeText: 'freeText',
    iconsFlow: 'iconsFlow',
    image: 'image',
    layout3Columns: 'layout-3-columns',
    layoutColumns: 'layout-columns',
    look: 'look',
    newsletter: 'newsletter',
    oneColumnWithBackground: 'oneColumnWithBackground',
    product: 'product',
    productCarousel: 'product-carousel',
    popup: 'popup',
    recommendedEinsteinProducts: 'recommendedEinsteinProducts',
    recommendedProducts: 'recommendedProducts',
    searchForm: 'searchForm',
    shopTheLook: 'shop-the-look',
    slider: 'slider',
    sliderAndText: 'slider-and-text',
    topSearch: 'top_search',
    bylPlayer: 'bylPlayer',
};

/* GA4 Constants */

var GA4 = {
    PAGE_TYPES: {
        account: 'account',
        campaign: 'campaign',
        cart: 'cart',
        category: 'category',
        checkout: 'checkout',
        home: 'home',
        info: 'info',
        login: 'login',
        payment: 'payment',
        product: 'product',
        product_look: 'product_look',
        product_look_pdp: 'product_look_pdp',
        purchase: 'purchase',
        search: 'search',
        searchModal: 'searchModal',
    },
};

var CONSENTMANAGER = {
    events: {
        /** @type {string} CMP code is loaded and the stub code has been removed. At this stage the CMP has no information about the consent yet. */
        init: 'init',
        /** @type {string} CMP finished loading its settings. If a consent string existed already, the consent data can now be read from the CMP. */
        settings: 'settings',
        /** @type {string} CMP shows the consent screen */
        consentscreen: 'consentscreen',
        /** @type {string} CMP removes the consent screen */
        consentscreenoff: 'consentscreenoff',
        /** @type {string} CMP shows the custom choices page */
        consentscreencustom: 'consentscreencustom',
        /** @type {string} CMP has gathered consent or a preexisting cookie was found. Consent data can now be read from the CMP. */
        consent: 'consent',
        /** @type {string} Visitor gave positive consent to all vendors/purposes */
        consentapproved: 'consentapproved',
        /** @type {string} Visitor rejected all vendors and purposes */
        consentrejected: 'consentrejected',
        /** @type {string} Visitor gave custom consent (accepts and rejections) */
        consentcustom: 'consentcustom',
        /** @type {string} Consent screen is shown, legitimate interest has been established for this visitor. */
        liestablished: 'liestablished',
        /** @type {string} Visitor accepted an dynamic content item. Vendor ID of the accepted item can be found in subtype variable. */
        vendorconsent: 'vendorconsent',
        /** @type {string} Various events for the IAB GPP standard */
        gpp: 'gpp',
        /** @type {string} Various events for the IAB TCF standard */
        tcfv2: 'tcfv2',
        /** @type {string} Once loading is finished (see event settings) and the system has decided to show the conesnt layer. */
        loadShowing: 'loadShowing',
        /** @type {string} Once loading is finished (see event settings) and the system has decided to not show the conesnt layer. */
        loadNotShowing: 'loadNotShowing',
    },
    vendorIDs: {
        /**
         * Vendor ID for `analytics_storage` consent type, which enables storage
         * (such as cookies) related to analytics e.g. visit duration.
         * @type {string}
         * @link https://help.consentmanager.es/books/cmp/page/working-with-google-consent-mode#bkmrk-vendor-id-google-con
         * @link https://support.google.com/tagmanager/answer/10718549?hl=en
         */
        googleAnalytics: 's26',
    },
};

module.exports = {
    CONSENTMANAGER: CONSENTMANAGER,
    EXPERIENCE_COMPONENT_TYPES: EXPERIENCE_COMPONENT_TYPES,
    PAGE_NAMES: PAGE_NAMES,
    PAGE_TYPES: PAGE_TYPES,
    PRODUCT_TILE_LOCATION: PRODUCT_TILE_LOCATION,
    GA4: GA4,
};
